import { useEffect, useState } from "react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { getStripe } from "@/lib/stripe";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onAdded: () => void;
}

const CardForm = ({ onAdded, onClose }: { onAdded: () => void; onClose: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not save card");
      return;
    }
    const paymentMethodId = typeof setupIntent?.payment_method === "string" ? setupIntent.payment_method : null;
    if (paymentMethodId) {
      const { data, error: defaultError } = await supabase.functions.invoke("manage-billing", {
        body: { action: "set_default_pm", payment_method_id: paymentMethodId },
      });
      if (defaultError || data?.error) {
        toast.error(data?.error || defaultError?.message || "Card saved, but could not make it default");
        return;
      }
    }
    toast.success("Card added");
    onAdded();
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save card"}
        </Button>
      </div>
    </form>
  );
};

const AddCardDialog = ({ open, onOpenChange, onAdded }: Props) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setClientSecret(null); return; }
    (async () => {
      setLoading(true);
      try {
        const [{ data, error }, s] = await Promise.all([
          supabase.functions.invoke("manage-billing", { body: { action: "create_setup_intent" } }),
          getStripe(),
        ]);
        if (error) throw error;
        setStripePromise(Promise.resolve(s));
        setClientSecret(data.client_secret);
      } catch (e: any) {
        toast.error(`Could not start card setup: ${e.message}`);
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a card</DialogTitle>
        </DialogHeader>
        {loading || !clientSecret || !stripePromise ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CardForm onAdded={onAdded} onClose={() => onOpenChange(false)} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddCardDialog;
